import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { teknikTokens, teknikButtons } from '../styles/teknik-tokens.js';

/**
 * Site-wide holding page.
 *
 * Rendered in place of the router by app-index.ts while SITE_UNDER_CONSTRUCTION
 * is true. Deliberately standalone — no header, no tab bar. Navigation to pages
 * that all show this same notice is just furniture.
 *
 * The two links out are the sites that are genuinely unaffected, so a visitor
 * who came here looking for Luna is one click from where they meant to go.
 */
@customElement('app-construction')
export class AppConstruction extends LitElement {
  static styles = [
    teknikTokens,
    teknikButtons,
    css`
      :host {
        display: block;
        font-family: var(--font-family);
        color: var(--tk-text);
        background: var(--tk-bg);
        min-height: 100vh;
      }

      @keyframes tk-rise {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: none; }
      }

      main {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4rem 24px;
        box-sizing: border-box;
      }

      .card {
        width: 100%;
        max-width: 560px;
        animation: tk-rise 0.5s ease both;
      }

      .brand {
        display: flex;
        align-items: center;
        gap: 11px;
        margin-bottom: 2.75rem;
      }

      /* teknik.svg is a 129x111 logomark — sized to its own aspect so mask-size
         contain doesn't leave dead space beside the glyph. */
      .mark {
        width: 30px;
        height: 26px;
        background: var(--tk-accent);
        -webkit-mask-image: url(/assets/icons/teknik.svg);
        mask-image: url(/assets/icons/teknik.svg);
        -webkit-mask-size: contain;
        mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        -webkit-mask-position: center;
        mask-position: center;
      }

      .wordmark {
        font-family: var(--font-display);
        font-size: 22px;
        font-weight: 600;
        letter-spacing: -0.005em;
      }

      .eyebrow {
        display: flex;
        align-items: center;
        gap: 0.9rem;
        margin-bottom: 1.4rem;
        font-family: var(--font-mono);
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--tk-accent);
      }
      .eyebrow::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--tk-border);
      }

      h1 {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: clamp(2.1rem, 6vw, 2.9rem);
        line-height: 1.06;
        letter-spacing: -0.02em;
        margin: 0 0 1.1rem;
        text-wrap: balance;
      }

      p {
        margin: 0;
        font-size: 1.0625rem;
        line-height: 1.65;
        color: var(--tk-muted);
        max-width: 52ch;
      }

      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.7rem;
        margin-top: 2.25rem;
      }

      .fine {
        margin-top: 2.75rem;
        padding-top: 1.4rem;
        border-top: 1px solid var(--tk-border);
        font-family: var(--font-mono);
        font-size: 11px;
        color: var(--tk-dim);
      }

      @media (prefers-reduced-motion: reduce) {
        .card { animation: none; }
      }
    `,
  ];

  render() {
    return html`
      <main>
        <div class="card">
          <div class="brand">
            <span class="mark" aria-hidden="true"></span>
            <span class="wordmark">Teknik</span>
          </div>

          <div class="eyebrow">Under construction</div>
          <h1>This site is being rebuilt.</h1>
          <p>
            teknik.sh is down for work. Luna's own site is live and unaffected. The dashboard,
            commands, plugins and developer docs all live there now.
          </p>

          <div class="actions">
            <a class="tk-btn primary" href="https://lunako.app">Go to lunako.app →</a>
            <a class="tk-btn secondary" href="https://sumiko.app">sumiko.app</a>
          </div>

          <div class="fine">Back soon.</div>
        </div>
      </main>
    `;
  }
}
